import { Event as Events } from '@planjs/utils';
import {
  SocketClient,
  ConnectionAdapter,
  SocketClientOptions,
  CloseMsg,
  ConnectionAdapterContext,
} from '../interfaces';
import { ReadyState } from '../enums';
import { Response } from '../response';

declare namespace wx {
  type IAnyObject = Record<string, any>;

  /** 接口调用结束的回调函数（调用成功、失败都会执行） */
  type ConnectSocketCompleteCallback = (res: GeneralCallbackResult) => void;
  /** 接口调用失败的回调函数 */
  type ConnectSocketFailCallback = (res: GeneralCallbackResult) => void;
  /** 接口调用成功的回调函数 */
  type ConnectSocketSuccessCallback = (res: GeneralCallbackResult) => void;
  /** 接口调用结束的回调函数（调用成功、失败都会执行） */
  type CloseSocketCompleteCallback = (res: GeneralCallbackResult) => void;
  /** 接口调用失败的回调函数 */
  type CloseSocketFailCallback = (res: GeneralCallbackResult) => void;
  /** 接口调用成功的回调函数 */
  type CloseSocketSuccessCallback = (res: GeneralCallbackResult) => void;
  /** 接口调用成功的回调函数 */
  type CloseSuccessCallback = (res: GeneralCallbackResult) => void;
  /** 接口调用结束的回调函数（调用成功、失败都会执行） */
  type CloseCompleteCallback = (res: GeneralCallbackResult) => void;
  /** 接口调用失败的回调函数 */
  type CloseFailCallback = (res: GeneralCallbackResult) => void;

  interface ConnectSocketOption {
    /** 开发者服务器 wss 接口地址 */
    url: string;
    /** 接口调用结束的回调函数（调用成功、失败都会执行） */
    complete?: ConnectSocketCompleteCallback;
    /** 接口调用失败的回调函数 */
    fail?: ConnectSocketFailCallback;
    /** HTTP Header，Header 中不能设置 Referer */
    header?: IAnyObject;
    /** 是否开启压缩扩展
     *
     * 最低基础库： `2.8.0` */
    perMessageDeflate?: boolean;
    /** 子协议数组
     *
     * 最低基础库： `1.4.0` */
    protocols?: string[];
    /** 接口调用成功的回调函数 */
    success?: ConnectSocketSuccessCallback;
    /** 建立 TCP 连接的时候的 TCP_NODELAY 设置
     *
     * 最低基础库： `2.4.0` */
    tcpNoDelay?: boolean;
    /** 超时时间，单位为毫秒
     *
     * 最低基础库： `2.10.0` */
    timeout?: number;
  }

  interface CloseOption {
    /** 一个数字值表示关闭连接的状态号，表示连接被关闭的原因。 */
    code?: number;
    /** 接口调用结束的回调函数（调用成功、失败都会执行） */
    complete?: CloseCompleteCallback;
    /** 接口调用失败的回调函数 */
    fail?: CloseFailCallback;
    /** 一个可读的字符串，表示连接被关闭的原因。这个字符串必须是不长于 123 字节的 UTF-8 文本（不是字符）。 */
    reason?: string;
    /** 接口调用成功的回调函数 */
    success?: CloseSuccessCallback;
  }

  interface SocketTaskOnCloseCallbackResult {
    /** 一个数字值表示关闭连接的状态号，表示连接被关闭的原因。 */
    code: number;
    /** 一个可读的字符串，表示连接被关闭的原因。 */
    reason: string;
  }

  interface SocketTaskOnErrorCallbackResult {
    /** 错误信息 */
    errMsg: string;
  }

  interface SocketTaskOnMessageCallbackResult {
    /** 服务器返回的消息 */
    data: string | ArrayBuffer;
  }

  interface GeneralCallbackResult {
    errMsg: string;
  }

  /** WebSocket 连接关闭事件的回调函数 */
  type SocketTaskOnCloseCallback = (result: SocketTaskOnCloseCallbackResult) => void;
  /** WebSocket 错误事件的回调函数 */
  type SocketTaskOnErrorCallback = (result: SocketTaskOnErrorCallbackResult) => void;
  /** WebSocket 接受到服务器的消息事件的回调函数 */
  type SocketTaskOnMessageCallback = (result: SocketTaskOnMessageCallbackResult) => void;
  /** WebSocket 连接打开事件的回调函数 */
  type OnOpenCallback = (result: OnOpenCallbackResult) => void;
  /** 接口调用结束的回调函数（调用成功、失败都会执行） */
  type SendCompleteCallback = (res: GeneralCallbackResult) => void;
  /** 接口调用失败的回调函数 */
  type SendFailCallback = (res: GeneralCallbackResult) => void;
  /** 接口调用成功的回调函数 */
  type SendSuccessCallback = (res: GeneralCallbackResult) => void;

  interface SocketTaskSendOption {
    /** 需要发送的内容 */
    data: string | ArrayBuffer;
    /** 接口调用结束的回调函数（调用成功、失败都会执行） */
    complete?: SendCompleteCallback;
    /** 接口调用失败的回调函数 */
    fail?: SendFailCallback;
    /** 接口调用成功的回调函数 */
    success?: SendSuccessCallback;
  }

  interface OnOpenCallbackResult {
    /** 连接成功的 HTTP 响应 Header
     *
     * 最低基础库： `2.0.0` */
    header: IAnyObject;
  }

  interface SocketTask {
    /** [SocketTask.close(Object object)](https://developers.weixin.qq.com/miniprogram/dev/api/network/websocket/SocketTask.close.html)
     *
     * 关闭 WebSocket 连接 */
    close(option: CloseOption): void;
    /** [SocketTask.onClose(function callback)](https://developers.weixin.qq.com/miniprogram/dev/api/network/websocket/SocketTask.onClose.html)
     *
     * 监听 WebSocket 连接关闭事件 */
    onClose(
      /** WebSocket 连接关闭事件的回调函数 */
      callback: SocketTaskOnCloseCallback
    ): void;
    /** [SocketTask.onError(function callback)](https://developers.weixin.qq.com/miniprogram/dev/api/network/websocket/SocketTask.onError.html)
     *
     * 监听 WebSocket 错误事件 */
    onError(
      /** WebSocket 错误事件的回调函数 */
      callback: SocketTaskOnErrorCallback
    ): void;
    /** [SocketTask.onMessage(function callback)](https://developers.weixin.qq.com/miniprogram/dev/api/network/websocket/SocketTask.onMessage.html)
     *
     * 监听 WebSocket 接受到服务器的消息事件 */
    onMessage(
      /** WebSocket 接受到服务器的消息事件的回调函数 */
      callback: SocketTaskOnMessageCallback
    ): void;
    /** [SocketTask.onOpen(function callback)](https://developers.weixin.qq.com/miniprogram/dev/api/network/websocket/SocketTask.onOpen.html)
     *
     * 监听 WebSocket 连接打开事件 */
    onOpen(
      /** WebSocket 连接打开事件的回调函数 */
      callback: OnOpenCallback
    ): void;
    /** [SocketTask.send(Object object)](https://developers.weixin.qq.com/miniprogram/dev/api/network/websocket/SocketTask.send.html)
     *
     * 通过 WebSocket 连接发送数据 */
    send(option: SocketTaskSendOption): void;
    readyState: ReadyState;
  }

  interface CloseSocketOption {
    /** 一个数字值表示关闭连接的状态号，表示连接被关闭的原因。 */
    code?: number;
    /** 接口调用结束的回调函数（调用成功、失败都会执行） */
    complete?: CloseSocketCompleteCallback;
    /** 接口调用失败的回调函数 */
    fail?: CloseSocketFailCallback;
    /** 一个可读的字符串，表示连接被关闭的原因。这个字符串必须是不长于 123 字节的 UTF-8 文本（不是字符）。 */
    reason?: string;
    /** 接口调用成功的回调函数 */
    success?: CloseSocketSuccessCallback;
  }

  function connectSocket(option: ConnectSocketOption): SocketTask;

  function closeSocket(option?: CloseSocketOption): void;
}

export class WXSocketClient implements SocketClient {
  readyState = ReadyState.CONNECTING;
  private task: wx.SocketTask;
  private events: Events;
  private readonly _removeListener!: () => void;
  constructor(options: SocketClientOptions, context?: ConnectionAdapterContext) {
    this.task = wx.connectSocket({
      url: options.url,
    });
    this.events = context?.events || new Events();
    this._updateReadyState();
    this._removeListener = this._listen();
  }

  private _updateReadyState(state?: ReadyState) {
    this.readyState = state !== undefined ? state : this.task.readyState;
    this.events.trigger('onReadyStateChange', this.task);
  }

  private _listen() {
    const onOpen = () => {
      this._updateReadyState();
      this.events.trigger('onOpen', this.task);
    };
    const onError = (err: wx.SocketTaskOnErrorCallbackResult) => {
      this._updateReadyState();
      this.events.trigger('onError', new Response(err.errMsg, false, err));
    };
    const onMessage = (res: wx.SocketTaskOnMessageCallbackResult) => {
      this.events.trigger('onMessage', new Response(res.data, true, res));
    };
    const onClose = (res: wx.SocketTaskOnCloseCallbackResult) => {
      this._updateReadyState();
      this.events.trigger('onClose', new Response(res.reason, res.code === 1000, res));
    };
    this.task.onOpen(onOpen);
    this.task.onClose(onClose);
    this.task.onError(onError);
    this.task.onMessage(onMessage);
    return () => {};
  }

  send(data: ArrayBuffer | string) {
    return new Promise<Response>((resolve, reject) => {
      this.task.send({
        data,
        success(res) {
          resolve(new Response(res.errMsg));
        },
        fail(err) {
          reject(new Response(err.errMsg, false));
        },
      });
    });
  }

  onClose(fn: (res: Response) => void): void {
    this.events.on('onClose', fn);
  }

  onError(fn: (res: Response) => void): void {
    this.events.on('onError', fn);
  }

  onMessage(fn: (res: Response) => void) {
    this.events.on('onMessage', fn);
  }

  onOpen(fn: () => void): void {
    this.events.on('onOpen', fn);
  }

  close(msg?: CloseMsg): void {
    this.task.close(msg!);
    this._removeListener();
  }
}

export class ConnectionWXSocket implements ConnectionAdapter {
  createConnection(options: SocketClientOptions, context: ConnectionAdapterContext): SocketClient {
    return new WXSocketClient(options, context);
  }
}
